import { NativeBiometric, BiometryType, BiometricOptions } from '@capgo/capacitor-native-biometric';
import { Capacitor } from '@capacitor/core';

class BiometricSecurity {
    private static instance: BiometricSecurity;

    public static getInstance(): BiometricSecurity {
        if (!BiometricSecurity.instance) {
            BiometricSecurity.instance = new BiometricSecurity();
        }
        
        return BiometricSecurity.instance;
    }

    /**
     * Check if biometric authentication is available on the device
     * @returns Promise with availability status and biometry type
     */
    public async isBiometricAvailable(): Promise<{
        isAvailable: boolean;
        biometryType?: BiometryType;
        error?: string;
    }> {
        try {
            // Skip if running in browser
            if (Capacitor.getPlatform() === 'web') {
                return { isAvailable: false, error: 'Biometric authentication not available in browser' };
            }

            const result = await NativeBiometric.isAvailable();
            return {
                isAvailable: result.isAvailable,
                biometryType: result.biometryType
            };
        } catch (error) {
            console.error('Error checking biometric availability:', error);
            return {
                isAvailable: false,
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    }

    /**
     * Verify identity with biometric authentication
     * @param options Biometric options (title, subtitle, description)
     * @returns Promise with authentication result
     */
    public async verifyIdentity(options: BiometricOptions = {
        title: 'Biometric Authentication',
        subtitle: 'Use your biometric credential to authenticate',
        description: 'Authenticate to access your wallet'
    }): Promise<{
        verified: boolean;
        error?: string;
    }> {
        try {
            // Skip if running in browser
            if (Capacitor.getPlatform() === 'web') {
                return { verified: false, error: 'Biometric authentication not available in browser' };
            }

            await NativeBiometric.verifyIdentity(options);
            return { verified: true };
        } catch (error) {
            console.error('Biometric verification failed:', error);
            return {
                verified: false,
                error: error instanceof Error ? error.message : 'Authentication failed'
            };
        }
    }

    /**
     * Store credentials securely with biometric protection
     * @param key Identifier for the credentials
     * @param username Username to store
     * @param password Password to store
     * @returns Promise with operation result
     */
    public async storeCredentials(key: string, username: string, password: string): Promise<{
        success: boolean;
        error?: string;
    }> {
        try {
            await NativeBiometric.setCredentials({
                username,
                password,
                server: key
            });
            return { success: true };
        } catch (error) {
            console.error('Error storing credentials:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to store credentials'
            };
        }
    }

    /**
     * Get credentials stored with biometric protection
     * @param key Identifier for the credentials
     * @returns Promise with credentials or error
     */
    public async getCredentials(key: string): Promise<{
        username?: string;
        password?: string;
        success: boolean;
        error?: string;
    }> {
        try {
            const credentials = await NativeBiometric.getCredentials({
                server: key
            });
            
            return {
                username: credentials.username,
                password: credentials.password,
                success: true
            };
        } catch (error) {
            console.error('Error retrieving credentials:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to retrieve credentials'
            };
        }
    }

    /**
     * Delete stored credentials
     * @param key Identifier for the credentials
     * @returns Promise with operation result
     */
    public async deleteCredentials(key: string): Promise<{
        success: boolean;
        error?: string;
    }> {
        try {
            await NativeBiometric.deleteCredentials({
                server: key
            });
            return { success: true };
        } catch (error) {
            console.error('Error deleting credentials:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to delete credentials'
            };
        }
    }
}

export default BiometricSecurity;

